import { useState, useRef, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import {
  HomeIcon,
  UsersIcon,
  ChartBarIcon,
  BookOpenIcon,
  QueueListIcon,
} from "@heroicons/react/24/solid";
import { useAuth } from "../../contexts/AuthContext";
import { isUnderAge } from "../../utils/dateUtils";

const BottomNavBar: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { userData } = useAuth();
  const [showLibraryMenu, setShowLibraryMenu] = useState(false);
  const [showDataMenu, setShowDataMenu] = useState(false);
  const [showDiaryMenu, setShowDiaryMenu] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const dataMenuRef = useRef<HTMLDivElement>(null);
  const diaryMenuRef = useRef<HTMLDivElement>(null);

  const isActive = (path: string) => location.pathname === path;

  // Social features are restricted to users aged 16 and above.
  const canAccessSocial = !isUnderAge(userData?.dob, 16);

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setShowLibraryMenu(false);
      }
    };
    if (showLibraryMenu) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [showLibraryMenu]);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (
        dataMenuRef.current &&
        !dataMenuRef.current.contains(e.target as Node)
      ) {
        setShowDataMenu(false);
      }
    };
    if (showDataMenu) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [showDataMenu]);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (
        diaryMenuRef.current &&
        !diaryMenuRef.current.contains(e.target as Node)
      ) {
        setShowDiaryMenu(false);
      }
    };
    if (showDiaryMenu) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [showDiaryMenu]);

  return (
    <div className="fixed bottom-0 left-0 right-0 bg-base-100 flex justify-around items-center h-24 shadow-[0_-4px_6px_-1px_rgba(0,0,0,0.05),0_-2px_4px_-1px_rgba(0,0,0,0.06)] pb-8 z-[60] sm:hidden">
      <Link to="/dashboard" className="inline-flex items-center space-x-2">
        <button
          className={`flex items-center ${
            isActive("/dashboard")
              ? "text-base-content"
              : "text-base-content/50"
          }`}
        >
          <HomeIcon className="h-6 w-6" />
        </button>
      </Link>

      <div className="relative" ref={diaryMenuRef}>
        {showDiaryMenu && (
          <>
            <div
              className="fixed inset-0 z-[65] backdrop-blur-sm bg-black/20"
              style={{ top: 0, bottom: "6rem" }}
              onClick={() => setShowDiaryMenu(false)}
            />
            <div className="absolute bottom-full right-1/2 translate-x-1/2 mb-8 z-[70] bg-base-100 rounded-lg shadow-xl border border-base-300 overflow-hidden min-w-[180px]">
              {[
                { label: "Training Diary", path: "/feed" },
                { label: "Sessions Completed", path: "/sessions/completed" },
                { label: "Performances", path: "/performances" },
              ].map((item, index, arr) => (
                <button
                  key={item.path}
                  onClick={() => {
                    navigate(item.path);
                    setShowDiaryMenu(false);
                  }}
                  className={`w-full px-4 py-3 text-left font-grotesk font-medium text-base-content hover:bg-base-200 transition-colors ${
                    isActive(item.path) ? "bg-base-200" : ""
                  } ${index < arr.length - 1 ? "border-b border-base-300" : ""}`}
                >
                  {item.label}
                </button>
              ))}
            </div>
          </>
        )}
        <button
          id="diary-title-mobile"
          onClick={() => setShowDiaryMenu((prev) => !prev)}
          className={`flex items-center  p-2 rounded-3xl ${
            isActive("/feed") ||
            isActive("/sessions/completed") ||
            isActive("/performances")
              ? "text-base-content"
              : "text-base-content/50"
          }`}
        >
          <BookOpenIcon className="h-6 w-6" />
        </button>
      </div>

      {canAccessSocial && (
        <Link to="/connections" className="inline-flex items-center space-x-2">
          <button
            className={`flex items-center ${
              isActive("/connections")
                ? "text-base-content"
                : "text-base-content/50"
            }`}
          >
            <UsersIcon className="h-6 w-6" />
          </button>
        </Link>
      )}

      {/* <Link
        to="/chats"
        className="inline-flex items-center space-x-2"
        id="wizard-mobile"
      >
        <button
          className={`flex items-center ${
            isActive("/chats") ? "text-base-content" : "text-base-content/50"
          }`}
        >
          <SparklesIcon className="h-6 w-6" />
        </button>
      </Link> */}

      {/* <Link to="/sessions" className="inline-flex items-center space-x-2">
        <button
          className={`flex items-center ${
            isActive("/sessions") ? "text-base-content" : "text-base-content/50"
          }`}
        >
          <ListBulletIcon className="h-6 w-6" />
        </button>
      </Link> */}

      <div className="relative" ref={menuRef}>
        {showLibraryMenu && (
          <>
            <div
              className="fixed inset-0 z-[65] backdrop-blur-sm bg-black/20"
              style={{ top: 0, bottom: "6rem" }}
              onClick={() => setShowLibraryMenu(false)}
            />
            <div className="absolute bottom-full right-1/2 translate-x-1/2 mb-8 z-[70] bg-base-100 rounded-lg shadow-xl border border-base-300 overflow-hidden min-w-[160px]">
              {[
                { label: "Sessions", path: "/sessions" },
                { label: "Exercises", path: "/activities" },
                { label: "Schedule", path: "/schedule" },
                { label: "Programs", path: "/programs" },
              ].map((item, index, arr) => (
                <button
                  key={item.path}
                  onClick={() => {
                    navigate(item.path);
                    setShowLibraryMenu(false);
                  }}
                  className={`w-full px-4 py-3 text-left font-grotesk font-medium text-base-content hover:bg-base-200 transition-colors ${
                    isActive(item.path) ? "bg-base-200" : ""
                  } ${index < arr.length - 1 ? "border-b border-base-300" : ""}`}
                >
                  {item.label}
                </button>
              ))}
            </div>
          </>
        )}
        <button
          onClick={() => setShowLibraryMenu((prev) => !prev)}
          className={`flex items-center ${
            isActive("/sessions") ||
            isActive("/activities") ||
            isActive("/schedule") ||
            isActive("/programs")
              ? "text-base-content"
              : "text-base-content/50"
          }`}
        >
          <QueueListIcon className="h-6 w-6" />
        </button>
      </div>

      {/* Data menu */}
      {!userData?.casualMode && (
        <div className="relative" ref={dataMenuRef}>
          {showDataMenu && (
            <>
              <div
                className="fixed inset-0 z-[65] backdrop-blur-sm bg-black/20"
                style={{ top: 0, bottom: "6rem" }}
                onClick={() => setShowDataMenu(false)}
              />
              <div className="absolute bottom-full right-0 mb-8 z-[70] bg-base-100 rounded-lg shadow-xl border border-base-300 overflow-hidden min-w-[200px]">
                {[
                  { label: "Training Load", path: "/insights/training" },
                  { label: "Wellness", path: "/insights/wellness" },
                  { label: "Peak Readiness", path: "/insights/peak" },
                  { label: "Health & Sleep", path: "/insights/sleep" },
                  {
                    label: "Activities & Exercises",
                    path: "/insights/activities",
                  },
                ].map((item, index, arr) => (
                  <button
                    key={item.path}
                    onClick={() => {
                      navigate(item.path);
                      setShowDataMenu(false);
                    }}
                    className={`w-full px-4 py-3 text-left font-grotesk font-medium text-base-content hover:bg-base-200 transition-colors ${
                      isActive(item.path) ? "bg-base-200" : ""
                    } ${index < arr.length - 1 ? "border-b border-base-300" : ""}`}
                  >
                    {item.label}
                  </button>
                ))}
              </div>
            </>
          )}
          <button
            onClick={() => setShowDataMenu((prev) => !prev)}
            className={`flex items-center ${
              isActive("/insights/training") ||
              isActive("/insights/wellness") ||
              isActive("/insights/peak") ||
              isActive("/insights/sleep") ||
              isActive("/insights/activities")
                ? "text-base-content"
                : "text-base-content/50"
            }`}
          >
            <ChartBarIcon aria-hidden="true" className="size-6" />
          </button>
        </div>
      )}
    </div>
  );
};

export default BottomNavBar;
